import { Controller } from "@hotwired/stimulus"

/**
 * Scan-to-entity host for the global search palette (CONEK-8571).
 *
 * The conek-ui command palette renders a Scan button when it is given a
 * `scan_url:`, and owns no camera: a click closes the palette and dispatches a
 * bubbling `command-palette:scan` event carrying `detail.scanUrl`. This
 * controller wraps the palette (NavbarHelper#render_command_palette) and does
 * the host's half:
 *
 *   1. `open` — on `command-palette:scan`, remember the URL and open the
 *      scanner of the `iot--qr-scan` controller on this same element (native
 *      `bridge--barcode` inside the Turbo Native app, html5-qrcode in a
 *      browser — the scanner the IoT pairing fields already use).
 *   2. `resolve` — on the scanner's `barcode:scanned`, visit
 *      `<scanUrl>?code=<value>`. ScanController#resolve redirects to the asset
 *      or sensor page, or back here with a flash when nothing matches.
 *      The scanner opts in to a loading state after the read (CONEK-9219,
 *      `loading-on-scan` in the partial) that the page change removes; when
 *      there is nothing to visit, `resolve` releases it (`stopLoading`).
 *
 * `iot--qr-scan` writes the decoded value into its hidden input target and
 * re-dispatches `barcode:scanned` from it with `detail.source ===
 * "iot--qr-scan"`. Only that event is acted on: the native bridge's own reply
 * (no source, possibly an `error` such as "cancelled") reaches this element
 * too, and acting on both would visit twice.
 *
 * @example
 *   <div id="command-palette-scan" class="contents"
 *        data-controller="scan-resolve iot--qr-scan bridge--barcode"
 *        data-iot--qr-scan-bridge--barcode-outlet="#command-palette-scan"
 *        data-action="command-palette:scan->scan-resolve#open
 *                     barcode:scanned->scan-resolve#resolve">
 *     <input type="hidden" data-iot--qr-scan-target="input">
 *     …the palette…
 *   </div>
 */
export default class extends Controller {
  connect() {
    this.scanUrl = null
  }

  open(event) {
    const scanUrl = event?.detail?.scanUrl
    if (typeof scanUrl !== "string" || scanUrl.length === 0) return

    const scanner = this.scanner()
    if (!scanner) {
      // Dev-only visibility today — the host wrapper (_command_palette_scan)
      // always pairs the two controllers, so this is only reachable under
      // broken configuration. We surface it to the operator too (not just
      // the browser console) so a misconfigured deploy fails loud instead
      // of making the scan button appear to do nothing.
      console.warn("scan-resolve: no iot--qr-scan controller on", this.element)
      this.dispatch("scanner-unavailable", { bubbles: true })
      return
    }

    this.scanUrl = scanUrl
    scanner.open()
  }

  resolve(event) {
    const detail = event?.detail || {}
    if (detail.source !== "iot--qr-scan") return

    const code = typeof detail.value === "string" ? detail.value.trim() : ""
    // A scan that was not started from the palette (or already resolved) has
    // nowhere to go. The scanner is waiting for a page that will not come
    // (CONEK-9219: it keeps a loading state up after a read), so release it.
    if (code.length === 0 || !this.scanUrl) {
      this.scanner()?.stopLoading?.()
      return
    }

    const url = this.resolveUrl(this.scanUrl, code)
    this.scanUrl = null
    this.visit(url)
  }

  scanner() {
    return this.application.getControllerForElementAndIdentifier(
      this.element,
      "iot--qr-scan",
    )
  }

  resolveUrl(scanUrl, code) {
    const url = new URL(scanUrl, window.location.origin)
    url.searchParams.set("code", code)
    return `${url.pathname}${url.search}`
  }

  // Turbo Drive where the page runs it — inside the Turbo Native app that is
  // what pushes the entity onto the native navigation stack. The Inertia
  // layout opts its <body> out of Drive (data-turbo="false"), and a Drive
  // visit there would swap the React root out from under Inertia, so it gets
  // a full page load instead.
  visit(url) {
    const driveOff = this.element.closest('[data-turbo="false"]') !== null
    if (window.Turbo && !driveOff) {
      window.Turbo.visit(url)
    } else {
      window.location.assign(url)
    }
  }
}
